import React, { useState, useEffect } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import { API_BASE_URL } from '../../config';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const cardStyle = {
  background: '#f3f4f6',
  borderRadius: '18px',
  boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
  padding: '18px 18px 10px 18px',
  minHeight: 200,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  alignItems: 'flex-start',
  overflow: 'hidden',
};

const metricCardStyle = {
  background: '#f3f4f6',
  borderRadius: '18px',
  boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
  padding: '18px 18px 10px 18px',
  minHeight: 120,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  alignItems: 'flex-start',
  overflow: 'hidden',
};

const getUserEmail = () => {
  try {
    const user = JSON.parse(localStorage.getItem('chatbotUser'));
    return user?.email || null;
  } catch {
    return null;
  }
};

const Analytics = () => {
  // Dynamic state for metrics
  const [callCount, setCallCount] = useState(0);
  const [callDuration, setCallDuration] = useState(0);
  const [callLatency, setCallLatency] = useState(0);
  const [callSuccessRate, setCallSuccessRate] = useState(0);
  const [rawCallData, setRawCallData] = useState([]);
  const [callCountData, setCallCountData] = useState({
    labels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
    datasets: [
      {
        label: 'Call Count',
        data: [0, 0, 0, 0, 0, 0, 0],
        borderColor: '#2563eb',
        backgroundColor: 'rgba(37, 99, 235, 0.1)',
        tension: 0.4,
        fill: true,
        pointRadius: 4,
        pointHoverRadius: 6,
      },
    ],
  });
  const [callDurationData, setCallDurationData] = useState({
    labels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
    datasets: [
      {
        label: 'Average Duration (seconds)',
        data: [0, 0, 0, 0, 0, 0, 0],
        backgroundColor: '#10b981',
        borderColor: '#10b981',
        borderWidth: 1,
        borderRadius: 4,
      },
    ],
  });
  const [callSuccessfulData, setCallSuccessfulData] = useState({
    labels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
    datasets: [
      {
        label: 'Successful Calls',
        data: [0, 0, 0, 0, 0, 0, 0],
        borderColor: '#10b981',
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        tension: 0.4,
        fill: true,
        pointRadius: 4,
        pointHoverRadius: 6,
      },
    ],
  });
  const [callPickupRateData, setCallPickupRateData] = useState({
    labels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
    datasets: [
      {
        label: 'Pickup Rate (%)',
        data: [0, 0, 0, 0, 0, 0, 0],
        borderColor: '#f59e0b',
        backgroundColor: 'rgba(245, 158, 11, 0.1)',
        tension: 0.4,
        fill: true,
        pointRadius: 4,
        pointHoverRadius: 6,
      },
    ],
  });

  // Fetch real call data from backend and calculate metrics
  useEffect(() => {
    const userEmail = getUserEmail();
    let intervalId;

    const fetchAndUpdate = () => {
      if (userEmail) {
        fetch(`${API_BASE_URL}/api/cached-calls?user_email=${encodeURIComponent(userEmail)}&limit=1000`)
          .then(res => res.json())
          .then(data => {
            if (Array.isArray(data)) {
              // Store raw call data for export
              setRawCallData(data);
              setCallCount(data.length);
              // Calculate total call duration (sum of all duration_ms)
              if (data.length > 0) {
                const totalDuration = data.reduce((sum, call) => sum + (call.duration_ms || 0), 0);
                const totalDurationSeconds = Math.round(totalDuration / 1000);
                setCallDuration(totalDurationSeconds);
              }
              // Calculate call success rate
              if (data.length > 0) {
                const successfulCalls = data.filter(call => call.duration_ms && call.duration_ms > 0).length;
                const successRate = Math.round((successfulCalls / data.length) * 100);
                setCallSuccessRate(successRate);
              }
              // Calculate call counts per day of week
              if (Array.isArray(data) && data.length > 0) {
                const dayCounts = [0, 0, 0, 0, 0, 0, 0]; // Mon-Sun
                data.forEach(call => {
                  if (call.start_timestamp) {
                    const date = new Date(call.start_timestamp * 1000);
                    // getDay(): 0=Sun, 1=Mon, ..., 6=Sat
                    let dayIdx = date.getDay();
                    // Shift so 0=Mon, ..., 6=Sun
                    dayIdx = (dayIdx + 6) % 7;
                    dayCounts[dayIdx]++;
                  }
                });
                setCallCountData(prev => ({
                  ...prev,
                  datasets: [{ ...prev.datasets[0], data: dayCounts }],
                }));
              }
              // Calculate average call duration per day of week
              if (Array.isArray(data) && data.length > 0) {
                const daySums = [0, 0, 0, 0, 0, 0, 0];
                const dayCounts = [0, 0, 0, 0, 0, 0, 0];
                data.forEach(call => {
                  if (call.start_timestamp) {
                    const date = new Date(call.start_timestamp * 1000);
                    let dayIdx = date.getDay();
                    dayIdx = (dayIdx + 6) % 7;
                    if (call.duration_ms && call.duration_ms > 0) {
                      daySums[dayIdx] += call.duration_ms;
                      dayCounts[dayIdx]++;
                    }
                  }
                });
                const dayAverages = daySums.map((sum, i) => dayCounts[i] > 0 ? Math.round(sum / dayCounts[i] / 1000) : 0);
                setCallDurationData(prev => ({
                  ...prev,
                  datasets: [{ ...prev.datasets[0], data: dayAverages }],
                }));
              }
              // Calculate successful calls per day of week
              if (Array.isArray(data) && data.length > 0) {
                const daySuccess = [0, 0, 0, 0, 0, 0, 0]; // Mon-Sun
                data.forEach(call => {
                  if (call.start_timestamp) {
                    const date = new Date(call.start_timestamp * 1000);
                    let dayIdx = date.getDay();
                    dayIdx = (dayIdx + 6) % 7;
                    if (call.duration_ms && call.duration_ms > 0) {
                      daySuccess[dayIdx]++;
                    }
                  }
                });
                setCallSuccessfulData(prev => ({
                  ...prev,
                  datasets: [{ ...prev.datasets[0], data: daySuccess }],
                }));
              }
              // Calculate pickup rate per day of week
              if (Array.isArray(data) && data.length > 0) {
                const dayTotal = [0, 0, 0, 0, 0, 0, 0];
                const dayPicked = [0, 0, 0, 0, 0, 0, 0];
                data.forEach(call => {
                  if (call.start_timestamp) {
                    const date = new Date(call.start_timestamp * 1000);
                    let dayIdx = date.getDay();
                    dayIdx = (dayIdx + 6) % 7;
                    dayTotal[dayIdx]++;
                    if (call.duration_ms && call.duration_ms > 0) {
                      dayPicked[dayIdx]++;
                    }
                  }
                });
                const dayRates = dayTotal.map((total, i) => total > 0 ? Math.round((dayPicked[i] / total) * 100) : 0);
                setCallPickupRateData(prev => ({
                  ...prev,
                  datasets: [{ ...prev.datasets[0], data: dayRates }],
                }));
              }
              // Calculate median call latency from top-level latency column (prefer call.latency, fallback to call_analysis.latency)
              if (data.length > 0) {
                const latencyValues = [];
                data.forEach(call => {
                  let latency = null;
                  if (typeof call.latency === 'number' && !isNaN(call.latency)) {
                    latency = call.latency;
                  } else if (typeof call.latency === 'string' && !isNaN(Number(call.latency))) {
                    latency = Number(call.latency);
                  } else if (call.call_analysis) {
                    try {
                      const analysis = typeof call.call_analysis === 'string' ? JSON.parse(call.call_analysis) : call.call_analysis;
                      if (typeof analysis.latency === 'number' && !isNaN(analysis.latency)) {
                        latency = analysis.latency;
                      } else if (typeof analysis.latency === 'string' && !isNaN(Number(analysis.latency))) {
                        latency = Number(analysis.latency);
                      }
                    } catch (e) {}
                  }
                  if (latency !== null && latency !== undefined) {
                    latencyValues.push(latency);
                  }
                });
                if (latencyValues.length > 0) {
                  latencyValues.sort((a, b) => a - b);
                  let medianLatency;
                  const mid = Math.floor(latencyValues.length / 2);
                  if (latencyValues.length % 2 === 0) {
                    medianLatency = Math.round((latencyValues[mid - 1] + latencyValues[mid]) / 2);
                  } else {
                    medianLatency = Math.round(latencyValues[mid]);
                  }
                  setCallLatency(medianLatency);
                }
              }
            }
          })
          .catch(() => {});
      }
    };

    fetchAndUpdate(); // Initial fetch
    intervalId = setInterval(fetchAndUpdate, 30000); // Poll every 30 seconds

    return () => clearInterval(intervalId);
  }, []);

  // Helper function to format date
  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp * 1000);
    return date.toLocaleString();
  };

  // Helper function to format duration
  const formatDuration = (durationMs) => {
    if (!durationMs || durationMs === 0) return '-';
    const seconds = Math.floor(durationMs / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`;
    }
    return `${remainingSeconds}s`;
  };

  // PDF export function
  const handleExportPDF = () => {
    if (rawCallData.length === 0) {
      alert('No analytics data to export.');
      return;
    }

    // Create CSV content with analytics data
    const headers = ['Date/Time', 'Agent ID', 'Duration', 'Status', 'Channel', 'End Reason', 'Call ID', 'Latency (ms)', 'Duration (ms)'];
    const csvContent = [
      headers.join(','),
      ...rawCallData.map(call => [
        formatDate(call.start_timestamp),
        call.agent_id || '-',
        formatDuration(call.duration_ms),
        call.call_status || '-',
        call.channel_type || '-',
        call.disconnection_reason || '-',
        call.call_id || '-',
        call.latency ? Math.round(call.latency) : '-',
        call.duration_ms || '-'
      ].join(','))
    ].join('\n');

    // Create and download CSV file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `analytics-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 100);
  };

  // Sample data for charts
  const callPickupData = {
    labels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
    datasets: [
      {
        label: 'Pickup Rate (%)',
        data: [85, 92, 78, 88, 95, 82, 89],
        borderColor: '#f59e0b',
        backgroundColor: 'rgba(245, 158, 11, 0.1)',
        tension: 0.4,
        fill: true,
        pointRadius: 4,
        pointHoverRadius: 6,
      },
    ],
  };

  const callSuccessData = {
    labels: ['Successful', 'Failed'],
    datasets: [
      {
        data: [callSuccessRate, 100 - callSuccessRate],
        backgroundColor: ['#10b981', '#ef4444'],
        borderWidth: 0,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: '#fff',
        borderWidth: 1,
        cornerRadius: 8,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          display: true,
          color: 'rgba(0, 0, 0, 0.05)',
        },
        ticks: {
          color: '#6b7280',
          font: {
            size: 10,
          },
        },
      },
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: '#6b7280',
          font: {
            size: 10,
          },
        },
      },
    },
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: '#fff',
        borderWidth: 1,
        cornerRadius: 8,
        callbacks: {
          label: function(context) {
            return context.label + ': ' + context.parsed + '%';
          }
        }
      },
      customCenterText: {
        text: `${callSuccessRate}%`,
        color: '#10b981',
        font: {
          size: '24',
          weight: 'bold'
        }
      }
    },
  };

  return (
    <div className="container d-flex flex-column h-100" style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', minHeight: 'calc(100vh - 80px)' }}>
      <div style={{ background: '#fff', borderRadius: '50px', boxShadow: '0 4px 24px rgba(0,0,0,0.07)', padding: '20px', minWidth: '900px', maxWidth: '1100px', width: '100%', color: '#111', marginTop: '0', marginBottom: '48px', height: 'auto', maxHeight: 'calc(100vh - 100px)', display: 'flex', flexDirection: 'column' }}>
        {/* Header row */}
        <div style={{ width: '100%', maxWidth: 1800, display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '0 auto 18px auto', padding: '0 8px' }}>
          <h2 style={{ fontWeight: 700, fontSize: '1.3rem', color: '#222', margin: 0 }}>Analytics</h2>
          <button 
            style={{ background: '#111827', color: '#fff', border: 'none', borderRadius: '8px', padding: '10px 22px', fontWeight: 600, fontSize: '1rem', cursor: 'pointer' }}
            onClick={handleExportPDF}
            title="Export Analytics Data as CSV"
          >
            Export <i className="fas fa-download" style={{ marginLeft: 8, fontSize: '0.95em' }}></i>
          </button>
        </div>
        {/* Analytics grid - scrollable */}
        <div style={{ flex: 1, overflowY: 'auto', width: '100%', maxWidth: 1800, margin: '0 auto', paddingBottom: '20px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gridGap: 18 }}>
            {/* Top row: 3 metrics + 1 donut */}
            <div style={{ ...metricCardStyle, gridColumn: 'span 1' }}>
              <div style={{ fontWeight: 600, fontSize: 16, color: '#374151', marginBottom: 8 }}>Call Count</div>
              <div style={{ fontWeight: 400, fontSize: 32, color: '#2563eb', marginBottom: 16 }}>{callCount}</div>
            </div>
            <div style={{ ...metricCardStyle, gridColumn: 'span 1' }}>
              <div style={{ fontWeight: 600, fontSize: 16, color: '#374151', marginBottom: 8 }}>Total Call Duration</div>
              <div style={{ fontWeight: 400, fontSize: 32, color: '#2563eb', marginBottom: 16 }}>{callDuration}s</div>
            </div>
            <div style={{ ...metricCardStyle, gridColumn: 'span 1' }}>
              <div style={{ fontWeight: 600, fontSize: 16, color: '#374151', marginBottom: 8 }}>Call Latency</div>
              <div style={{ fontWeight: 400, fontSize: 32, color: '#2563eb', marginBottom: 16 }}>{callLatency}ms</div>
            </div>
            <div style={{ ...cardStyle, gridColumn: 'span 1', alignItems: 'center' }}>
              <div style={{ fontWeight: 600, fontSize: 16, color: '#374151', marginBottom: 8, textAlign: 'center' }}>Call Success Rate</div>
              <div style={{ width: 100, height: 100 }}>
                <Doughnut data={callSuccessData} options={doughnutOptions} />
              </div>
            </div>
            {/* 2nd row: 2 charts - Call Counts and Average Call Duration */}
            <div style={{ ...cardStyle, gridColumn: 'span 2' }}>
              <div style={{ fontWeight: 600, fontSize: 16, color: '#374151', marginBottom: 12 }}>Call Counts</div>
              <div style={{ width: '100%', height: 120 }}>
                <Line data={callCountData} options={chartOptions} />
              </div>
            </div>
            <div style={{ ...cardStyle, gridColumn: 'span 2' }}>
              <div style={{ fontWeight: 600, fontSize: 16, color: '#374151', marginBottom: 12 }}>Average Call Duration</div>
              <div style={{ width: '100%', height: 120 }}>
                <Bar data={callDurationData} options={chartOptions} />
              </div>
            </div>
            {/* 3rd row: 2 charts - Call Successful and Call Pickup Rate */}
            <div style={{ ...cardStyle, gridColumn: 'span 2' }}>
              <div style={{ fontWeight: 600, fontSize: 16, color: '#374151', marginBottom: 12 }}>Call Successful</div>
              <div style={{ width: '100%', height: 120 }}>
                <Line data={callSuccessfulData} options={chartOptions} />
              </div>
            </div>
            <div style={{ ...cardStyle, gridColumn: 'span 2' }}>
              <div style={{ fontWeight: 600, fontSize: 16, color: '#374151', marginBottom: 12 }}>Call Pickup Rate</div>
              <div style={{ width: '100%', height: 120 }}>
                <Line data={callPickupRateData} options={chartOptions} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Analytics; 